import { List, Map } from 'immutable';
import { createSelector } from 'reselect';

import { getBillableInvoiceItemsByProjectId } from 'app-components/flux//billable-invoice-items/billableInvoiceItemsSelectors';
// biome-ignore lint/suspicious/noImportCycles: SIMPL-29722
import { selectUnbilledProjectExpenses } from 'app-components/flux/transactions/transactionsSelectors';
// biome-ignore lint/suspicious/noImportCycles: SIMPL-29722
import { calculateBillablePlusTxnSum } from 'app-components/flux/transactions/transactionsUtils';
import type RootState from 'app-components/utils/redux-store/rootState';

import { mkProject, PROJECTS_REDUCER_KEY, type Project, type ProjectStatus } from './projectsTypes';

type State = Pick<RootState, typeof PROJECTS_REDUCER_KEY>;

export const getProjectsStore = (state: State) => state[PROJECTS_REDUCER_KEY];
export const getProjects = (state: State) => getProjectsStore(state).resourcesById;
export const getLoadPending = (state: State) => getProjectsStore(state).loadPending;
export const getIsLoading = (state: State) => getProjectsStore(state).isLoading;
export const getError = (state: State) => getProjectsStore(state).error;
export const getLastSyncDate = (state: State) => getProjectsStore(state).lastSyncDate;

export const getProjectById = createSelector(
  getProjects,
  (_state: unknown, projectId: string) => projectId,
  (projects, id) => projects?.get(id),
);

export const getProjectsByCustomerId = createSelector(getProjects, (projects) => {
  type ProjectsByStatus = {
    allProjects?: List<Project>;
    completedProjects?: List<Project>;
    activeProjects?: List<Project>;
  };

  type ProjectsMap = Map<string, ProjectsByStatus>;

  return projects.reduce((resultMap: ProjectsMap, project: Project) => {
    const customerId = project.customerId ?? 'nulled-customer-id';
    const status = project.status as ProjectStatus;

    const projectObject: ProjectsByStatus = {
      allProjects: List([mkProject(project)]),
      completedProjects: status === 'COMPLETED' ? List([mkProject(project)]) : List(),
      activeProjects: status === 'IN_PROGRESS' || status === 'PAST_DUE' ? List([mkProject(project)]) : List(),
    };

    if (resultMap.has(customerId)) {
      return resultMap.update(customerId, (existingProjectObject) => ({
        allProjects: existingProjectObject?.allProjects?.push(mkProject(project)),
        completedProjects:
          status === 'COMPLETED'
            ? existingProjectObject?.completedProjects?.push(mkProject(project))
            : existingProjectObject?.completedProjects,
        activeProjects:
          status === 'IN_PROGRESS' || status === 'PAST_DUE'
            ? existingProjectObject?.activeProjects?.push(mkProject(project))
            : existingProjectObject?.activeProjects,
      }));
    } else {
      return resultMap.set(customerId, projectObject);
    }
  }, Map<string, ProjectsByStatus>());
});

export const getUnbilledProjectsByCustomerId = createSelector(
  [
    (state, customerId): List<Project> =>
      getProjectsByCustomerId(state).get(customerId ?? '')?.activeProjects || List(),
    getBillableInvoiceItemsByProjectId,
    (state) => state,
    (_, customerId) => customerId,
  ],
  (activeProjects, billableItemsByProjectId, state, customerId) =>
    activeProjects.flatMap((project: Project) => {
      const projectId = project.id ?? 'no-project';
      const billableItems = billableItemsByProjectId.get(projectId) || [];

      const relatedTransactions = selectUnbilledProjectExpenses(state, {
        customerId,
        projectId,
      });

      const unbilledSum = calculateBillablePlusTxnSum(billableItems, relatedTransactions.toArray());

      return unbilledSum > 0
        ? [
            {
              id: project.id,
              name: project.name,
              unbilled: unbilledSum,
            },
          ]
        : [];
    }),
);

export const getProjectNameById = createSelector(
  getProjects,
  (_state: unknown, projectId: string) => projectId,
  (projects, id) => projects?.get(id)?.name ?? '',
);

export const getNumberOfActiveProjects = createSelector(
  getProjects,
  (projects) => projects.filter((project) => project.status === 'IN_PROGRESS' || project.status === 'PAST_DUE').size,
);

export const getTotalNumberOfProjects = createSelector(getProjects, (projects) => projects.size);
